import sjcl from "sjcl";

import Chip from "@mui/material/Chip";

import { httpGet, httpPost, httpPut, httpDelete, generateUUID } from "./AbstractService";
import { getUserListMembers } from "./UserService";

import clone from "lodash-es/clone";
import isEmpty from "lodash-es/isEmpty";

import { getYYYYMMDD } from "./DateService";
import { paddy } from "./StringService";
import { getPeriodDetails } from "./SchoolService";
import ENDPOINTS from "../data/endpoints.json";

export const ACTIVITY_QR_KEY = "cJCUL653LPOReKznlOtpzo7LVuyPlim2";

/**
 * returns activites and proctored_activities
 * @param {*} school
 * @param {*} jwt
 * @param {*} yyyymmdd
 * @returns
 */
export const getDayForTeacher = (school, jwt, yyyymmdd = null) => {
  // use current date
  if (isEmpty(yyyymmdd)) {
    yyyymmdd = getYYYYMMDD();
  }

  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].activity}/teacher/activity/date/${yyyymmdd}`
  );
};

export const getTeacherThisWeek = (school, jwt) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].activity}/teacher/activity/this-week`
  );
};

export const getTeacherNextWeek = (school, jwt) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].activity}/teacher/activity/next-week`
  );
};

export const getTeacherActivities = (school, jwt, teacherEid = null) => {
  let url = `${ENDPOINTS[school.region].activity}/teacher/activity`;
  if (teacherEid != null) {
    url += `?eid=${teacherEid}`;
  }
  return httpGet(jwt, url);
};

export const getTeacherActivitiesDetailed = (school, jwt, teacherEid) => {
  return httpGet(
    jwt,
    `${
      ENDPOINTS[school.region].activity
    }/teacher/activity?detail=1&eid=${teacherEid}`
  );
};

export const getActivityDate = (school, jwt, eid) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].activity}/activity-date/${eid}`);
};

export const getActivityDateDetails = (school, jwt, eid) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity-date/${eid}?details=1`
  );
};

export const getDayForStudent = (
  school,
  jwt,
  yyyymmdd = null,
  studentEid = null
) => {
  // use current date
  if (isEmpty(yyyymmdd)) {
    yyyymmdd = getYYYYMMDD();
  }

  let url = `${
    ENDPOINTS[school.region].activity
  }/user/activity/date/${yyyymmdd}`;

  if (studentEid != null) {
    url += "?q=" + studentEid;
  }

  return httpGet(jwt, url);
};

export const getDateActivities = (
  school,
  jwt,
  yyyymmdd = null,
  studentEid = null
) => {
  // use current date
  if (isEmpty(yyyymmdd)) {
    yyyymmdd = getYYYYMMDD();
  }

  let url = `${ENDPOINTS[school.region].activity}/sad/date/${yyyymmdd}`;

  if (studentEid != null) {
    url += "?q=" + studentEid;
  }

  return httpGet(jwt, url);
};

export const getHistoryForStudent = (school, jwt, dateStart, dateEnd) => {
  return httpGet(
    jwt,
    `${
      ENDPOINTS[school.region].activity
    }/user/activity/future?dateStart=${dateStart}&dateEnd=${dateEnd}`
  );
};

export const saveSuggestion = (school, jwt, name, description, teacherEid) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity/suggest`,
    {
      name,
      description,
      teacherEid,
    }
  );
};

export const getCategories = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].activity}/activity/category`);
};

/**
 *
 * @param {*} activities map: key: periodX, value: array
 * @param {*} periodNum
 */
export const getSelectedActivityFromResult = (activities, periodNum) => {
  const key = `period${periodNum}`;
  if (key in activities) {
    const list = activities[key];
    if (list != null && list.length > 0) {
      return list[0];
    }
  }

  return null;
};

export const saveStudentDay = (school, jwt, values) => {
  values["requestEid"] = generateUUID();
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].activity}/student/date/${values.date}`,
    values
  );
};

export const searchActivities = (
  school,
  jwt,
  dateStart,
  dateEnd,
  categoryEid,
  teacherEid,
  period,
  tagEid
) => {
  // search is a slug to the /activity service
  return httpGet(
    jwt,
    `${
      ENDPOINTS[school.region].activity
    }/activity/search?dateStart=${dateStart}&dateEnd=${dateEnd}&category=${categoryEid}&teacher=${teacherEid}&period=${period}&tag=${tagEid}`
  );
};

export const sendActivityDateMessage = (school, jwt, activityDateEid, message) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity-date/${activityDateEid}/message`,
    {
      message,
    }
  );
}

export const addStudentsToActivityDate = (
  school,
  jwt,
  activityDateEid,
  studentEids,
  markPresent = false
) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity-date/${activityDateEid}/attendee`,
    {
      studentEids,
      markPresent
    }
  );
};

export const validateStudentsToActivityDate = (
  school,
  jwt,
  activityDateEid,
  studentEids
) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity-date/${activityDateEid}/attendee-validate`,
    {
      studentEids
    }
  );
};

export const addStudentListToActivityDate = (
  school,
  jwt,
  activityDateEid,
  studentListId
) => {
  return getUserListMembers(school, jwt, studentListId).then((results) => {
    // get list of members, then send to add
    const members = [];
    for (const entry of results.data) {
      members.push(entry.eid);
    }

    return addStudentsToActivityDate(school, jwt, activityDateEid, members);
  });
};

export const getActivityDateStudents = (school, jwt, activityDateEid) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity-date/${activityDateEid}/attendee`,
    {}
  );
};

export const updateSADStudentModify = (school, jwt, sadEid, isModify) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].activity}/sad/${sadEid}/student-modify`,
    {
      modify: isModify,
    }
  );
};

export const updateSADTeacherModify = (school, jwt, sadEid, isModify) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].activity}/sad/${sadEid}/teacher-modify`,
    {
      modify: isModify,
    }
  );
};

export const removeSAD = (school, jwt, sadEid) => {
  return httpDelete(jwt, `${ENDPOINTS[school.region].activity}/sad/${sadEid}`);
};

export const updateADStudentModify = (school, jwt, adEid, isModify) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity-date/${adEid}/student-modify`,
    {
      modify: isModify,
    }
  );
};

export const updateADTeacherModify = (school, jwt, adEid, isModify) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity-date/${adEid}/teacher-modify`,
    {
      modify: isModify,
    }
  );
};

export const createADUserList = (school, jwt, adEid, name) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity-date/${adEid}/user-list`,
    {
      name,
    }
  );
};

export const updateSADTeacherComment = (
  school,
  jwt,
  sadEid,
  teacherComment
) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].activity}/sad/${sadEid}/comment-modify`,
    {
      teacherComment,
    }
  );
};

// used for transfer
export const getActivityDateAvailable = (school, jwt, activityDateEid) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity-date/${activityDateEid}/available`
  );
};

export const transferStudentOut = (
  school,
  jwt,
  newActivityDateEid,
  oldStudentActivityDateEid,
  studentEid,
  createHallPass
) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity/transfer/out`,
    {
      newActivityDateEid,
      oldStudentActivityDateEid,
      studentEid,
      createHallPass
    }
  );
};

export const updateAttendance = (
  school,
  jwt,
  activityDateEid,
  presentEids,
  lateEids,
  absentEids,
  lateToTardy
) => {
  return httpPost(
    jwt,
    `${
      ENDPOINTS[school.region].activity
    }/activity/date/attendance/delta/${activityDateEid}`,
    {
      absent_eids: absentEids,
      present_eids: presentEids,
      late_eids: lateEids,
      late_to_tardy: lateToTardy
    }
  );
};

export const getStudentActivitiesByFaculty = (
  school,
  jwt,
  studentEid,
  date = null
) => {
  let url = `${
    ENDPOINTS[school.region].activity
  }/activity/student/${studentEid}`;

  if (date != null) {
    url += `?date=${date}`;
  }

  return httpGet(jwt, url);
};

export const getTeacherRoster = (school, jwt, date) => {
  let url = `${ENDPOINTS[school.region].activity}/teacher/roster?date=${date}`;
  return httpGet(jwt, url);
};

/**
 * other teacher eid is used to read another teacher; if null, then we will use the teacher id in the jwt
 * @param {*} school
 * @param {*} jwt
 * @param {*} startDate
 * @param {*} endDate
 * @param {*} otherTeacherEid
 * @returns
 */
export const getTeacherCalendar = (
  school,
  jwt,
  startDate,
  endDate,
  otherTeacherEid = null
) => {
  let url = `${
    ENDPOINTS[school.region].activity
  }/teacher/calendar?start=${startDate}&end=${endDate}`;

  if (otherTeacherEid != null) {
    url += `&oteid=${otherTeacherEid}`;
  }

  return httpGet(jwt, url);
};

export const getTeacherActivityDates = (
  school,
  jwt,
  startDate,
  endDate,
  teacherEid = null
) => {
  let url = `${
    ENDPOINTS[school.region].activity
  }/activity-date?start=${startDate}&end=${endDate}`;

  if (teacherEid != null) {
    url += `&teacherEid=${teacherEid}`;
  }

  return httpGet(jwt, url);
};

export const saveTeacherDay = (school, jwt, values) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].activity}/teacher/calendar/date`,
    values
  );
};

export const validateTeacherDay = (school, jwt, values) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].activity}/teacher/calendar/date/validate`,
    values
  );
};

export const deleteActivityDates = (school, jwt, activityDateEids) => {
  return httpPost(jwt, `${ENDPOINTS[school.region].activity}/activity-date/delete`, {
    activityDateEids,
  });
};

export const repeatActivity = (
  school,
  jwt,
  activityEid,
  period,
  dateStart,
  dateEnd,
  days,
  selectedDays,
  teacherEid = null
) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].activity}/teacher/activity/repeat`,
    {
      activityEid,
      period,
      dateStart,
      dateEnd,
      days,
      selectedDays,
      teacherEid,
    }
  );
};

export const createActivity = (school, jwt, values) => {
  return httpPost(jwt, `${ENDPOINTS[school.region].activity}/activity`, values);
};

export const updateActivity = (school, jwt, eid, values) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity/${eid}`,
    values
  );
};

export const getActivity = (school, jwt, eid) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].activity}/activity/${eid}`);
};

export const getActivityDateByEidTime = (school, jwt, eid) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity/${eid}/find/date-time`
  );
};

export const deleteActivity = (school, jwt, eid) => {
  return httpDelete(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity/${eid}`
  );
};

export const getTeacherSuggestedActivities = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].activity}/teacher/suggest`);
};

// 0 == reviewed, 100, reviewed+create
export const updateTeacherSuggestedActivity = (school, jwt, eid, status) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].activity}/teacher/suggest`, {
    eid,
    status,
  });
};

export const getSchoolActivities = (
  school,
  jwt,
  catEid = null,
  orderBy = null,
  date = null
) => {
  let url = `${ENDPOINTS[school.region].activity}/school/activity?x=1`;

  if (catEid != null) {
    url += `&catEid=${catEid}`;
  }
  if (orderBy != null) {
    url += `&orderBy=${orderBy}`;
  }
  if (date != null) {
    url += `&date=${date}`;
  }
  return httpGet(jwt, url);
};

export const getActivityDates = (school, jwt, eid) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity/${eid}/date`
  );
};

export const saveTeacherRecommendation = (
  school,
  jwt,
  activityEid,
  activityDateEids,
  dateStart,
  dateEnd,
  studentEids
) => {
  const values = {
    activityEid,
    activityDateEids,
    dateStart,
    dateEnd,
    studentEids,
  };
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].activity}/teacher/activity/recommend`,
    values
  );
};

export const getTeacherRecommendations = (school, jwt) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].activity}/teacher/activity/recommend`
  );
};

export const removeTeacherRecommendation = (school, jwt, id) => {
  return httpDelete(
    jwt,
    `${ENDPOINTS[school.region].activity}/teacher/activity/recommend/${id}`
  );
};

export const getDefaultHistory = (school, jwt, date = null) => {
  let url = `${ENDPOINTS[school.region].activity}/oxTvFZWY936B78W4sxzU`;

  if (date != null) {
    url += "?date=" + date;
  }
  return httpGet(jwt, url);
};

export const runDefault = (school, jwt, date) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].activity}/oxTvFZWY936B78W4sxzU`,
    {
      date,
      action: "default",
    }
  );
};

export const resetDefault = (school, jwt, date) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].activity}/oxTvFZWY936B78W4sxzU`,
    {
      date,
      action: "reset",
    }
  );
};

export const getBulkDelete = (
  school,
  jwt,
  startDate,
  endDate,
  studentEid,
  studentListEid,
  activityEid,
  period,
  orderBy
) => {
  let url = `${
    ENDPOINTS[school.region].school
  }/rpt/activity/bulk-delete?startDate=${startDate}&endDate=${endDate}&studentEid=${studentEid}&activityEid=${activityEid}&period=${period}&orderBy=${orderBy}&studentListEid=${studentListEid}`;
  return httpGet(jwt, url);
};

export const deleteSADs = (school, jwt, sadEids) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].activity}/sad/all/delete`, {
    eids: sadEids,
  });
};

export const bulkLookup = (
  school,
  jwt,
  activityEid,
  dateStart,
  dateEnd,
  period,
  days
) => {
  let url = `${
    ENDPOINTS[school.region].activity
  }/bulk/lookup?startDate=${dateStart}&endDate=${dateEnd}&activityEid=${activityEid}&period=${period}&days=${days}`;
  return httpGet(jwt, url);
};

export const bulkAdd = (
  school,
  jwt,
  studentEids,
  activityDateEids,
  studentModify,
  teacherModify
) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].activity}/bulk/add`, {
    studentEids,
    activityDateEids,
    studentModify,
    teacherModify,
  });
};

export const encryptActivitySignup = (key, adEid) => {
  const value = `${adEid}`;
  return btoa(sjcl.encrypt(key, value));
};

export const decryptActivitySignup = (key, value) => {
  return sjcl.decrypt(key, atob(value));
};

export const encryptActivityAttend = (key, adEid) => {
  const value = `${adEid}`;
  return btoa(sjcl.encrypt(key, value));
};

export const decryptActivityAttend = (key, value) => {
  return sjcl.decrypt(key, atob(value));
};

export const doSingleSignup = (school, jwt, activityDateEid) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].activity}/sad/signup`, {
    activityDateEid,
  });
};

export const doSingleSignupAttend = (school, jwt, activityDateEid) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].activity}/sad/signup-attend`, {
    activityDateEid,
  });
};

export const doAttend = (school, jwt, activityDateEid) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].activity}/sad/attend`, {
    activityDateEid,
  });
};

export const updateProctorsToActivityDate = (
  school,
  jwt,
  activityDateEid,
  teacherEids
) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity-date/${activityDateEid}/proctor`,
    {
      teacherEids,
    }
  );
};

export const changeOwner = (school, jwt, activityEid, ownerEid) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity/${activityEid}/owner`,
    { ownerEid }
  );
};

export const cloneActivity = (
  school,
  jwt,
  activityEid,
  name,
  restrictedEnrollment,
  activityDates
) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity/${activityEid}/clone`,
    {
      name,
      restrictedEnrollment,
      activityDates,
    }
  );
};

export const getRestrictedEnrollmentStudents = (school, jwt, activityEid) => {
  return httpGet(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity/${activityEid}/re`
  );
};

export const createActivityUserList = (school, jwt, activityEid, name) => {
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity/${activityEid}/user-list`,
    {
      name,
    }
  );
};

export const getSchoolTags = (school, jwt) => {
  return httpGet(jwt, `${ENDPOINTS[school.region].activity}/tag`);
};

// creates an event object to be used for the daily/weekly view with FullCalendar
export const createEventEntry = (school, date, activityDate) => {
  const event = clone(activityDate);

  // set the fullcalendar fields
  event.title = event.name;
  // signup date is the global input value passed to this component
  // also need to append hh:mm:ss to the signup date for
  event.start = `${date} ${getStartTime(school, activityDate)}`;
  event.end = `${date} ${getEndTime(school, activityDate)}`;
};

const getStartTime = (school, activityDate) => {
  const schoolPeriodDetails = getPeriodDetails(school, activityDate.period - 1);
  return `${paddy(schoolPeriodDetails.startHour, 2, "0")} ${paddy(
    schoolPeriodDetails.startMinute,
    2,
    "0"
  )}`;
};

// factors in the periodLength
const getEndTime = (school, activityDate) => {
  const schoolPeriodDetails = getPeriodDetails(
    school,
    activityDate.period - 1 + activityDate.periodLength
  );
  return `${paddy(schoolPeriodDetails.endHour, 2, "0")} ${paddy(
    schoolPeriodDetails.endMinute,
    2,
    "0"
  )}`;
};

export const updateActivityLocations = (
  school,
  jwt,
  activityEid,
  locationEid = null
) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity/${activityEid}/location`,
    {
      locationEid,
    }
  );
};

export const getAttendanceTakenText = (attendanceTaken) => {
  let attendanceTakenText = "YES";
  if (attendanceTaken === 0) {
    attendanceTakenText = "NO";
  }
  return attendanceTakenText;
};

export const getPresentAbsentLateChip = (attendanceTaken, isLate) => {
  if (attendanceTaken === 0) {
    return <Chip label="N/A" sx={{ mr: 0.5 }} color="default" />;
  } else {
    if (isLate === 0) {
      return <Chip label="PRESENT" sx={{ mr: 0.5 }} color="success" />;
    } else if (isLate === 1) {
      return <Chip label="LATE" sx={{ mr: 0.5 }} color="warning" />;
    }
  }
  return <Chip label="ABSENT" sx={{ mr: 0.5 }} color="danger" />;
};

export const getPresentAbsentLateText = (attendanceTaken, isLate) => {
  if (attendanceTaken === 0) {
    return "N/A";
  } else {
    if (isLate === 0) {
      return "PRESENT";
    } else if (isLate === 1) {
      return "LATE";
    }
  }
  return "ABSENT";
};

export const getActivityCategories = (school, jwt, categoryEid) => {
  return httpGet(
    jwt,
    `${
      ENDPOINTS[school.region].activity
    }/school/category/${categoryEid}/activity`
  );
};

export const changeLocation = (school, jwt, activityEid, locationEid) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity/${activityEid}/location`,
    { locationEid }
  );
};

export const adminUpdateActivity = (
  school,
  jwt,
  eid,
  maxAttendance,
  markAllPresent
) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity/${eid}/bulk`,
    { maxAttendance, markAllPresent }
  );
};

export const getLockedStudents = (school, jwt, yyyymmdd = null) => {
  // use current date
  if (isEmpty(yyyymmdd)) {
    yyyymmdd = getYYYYMMDD();
  }

  let url = `${ENDPOINTS[school.region].activity}/sad/locked?date=${yyyymmdd}`;
  return httpGet(jwt, url);
};

export const adminUnlockStudent = (school, jwt, sadEid) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].activity}/sad/locked`, {
    action: "unlock",
    sadEid,
  });
};

export const getFutureActivityDates = (school, jwt, adEid) => {
  let url = `${ENDPOINTS[school.region].activity}/activity/${adEid}/future`;
  return httpGet(jwt, url);
};

export const changeActivityDateLocation = (
  school,
  jwt,
  activityDateEid,
  locationEid
) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity-date/${activityDateEid}/location`,
    { locationEid }
  );
};


export const getSchoolActivityDates = (
  school,
  jwt,
  date = null
) => {
  let url = `${ENDPOINTS[school.region].activity}/school/activity-date?date=${date}`;
  return httpGet(jwt, url);
};

export const cloneSchoolActivityDates = (
  school,
  jwt,
  destinationDate,
  activityDateIds,
  includeAttendees
) => {
  let url = `${ENDPOINTS[school.region].activity}/school/activity-date`;
  return httpPut(jwt, url, {
    destinationDate, 
    activityDateIds,
    includeAttendees
  });
};

export const saveActivityColor = (school, jwt, activityEid, hexColor) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity/${activityEid}/color`,
    {
      hexColor
    }
  );
};

export const doAttendByTeacher = (school, jwt, studentEid, activityDateEid) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].activity}/sad/attend/teacher`, {
    studentEid,
    activityDateEid,
  });
};


export const getStudentActivitiesByDatePeriod = (school, jwt, yyyymmdd, period) => {
  let url = `${ENDPOINTS[school.region].activity}/activity/date/${yyyymmdd}/period/${period}/student`;
  return httpGet(jwt, url);
}

export const getActivitiesByDatePeriod = (school, jwt, yyyymmdd, period) => {
  let url = `${ENDPOINTS[school.region].activity}/activity/date/${yyyymmdd}/period/${period}`;
  return httpGet(jwt, url);
}

export const saveSadsByDatePeriod = (school, jwt, date, activityDateEid, selectedStudentEids) => {
  let url = `${ENDPOINTS[school.region].activity}/activity/date/period/signup`;
  return httpPost(jwt, url, {
    date: date,
    activityDateEid: activityDateEid,
    studentEids: selectedStudentEids
  });
}

export const getPresignedActivityFileUrl = (school, jwt, newFileUuid, filename) => {
  const values = {
    newFileUuid,
    filename,
  };
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity/new/file/url`,
    values
  );
};

export const saveActivityFile = (school, jwt, newFileUuid, filename, saveFilename, name, description, mimeType, size) => {
  const values = {
    newFileUuid,
    filename,
    saveFilename,
    name,
    description,
    mimeType,
    size,
  };
  return httpPost(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity/new/file/save`,
    values
  );
};

export const changeLimitDays = (school, jwt, activityEid, limitDays) => {
  return httpPut(
    jwt,
    `${ENDPOINTS[school.region].activity}/activity/${activityEid}/limit-days`,
    { limitDays }
  );
};

export const deleteADAttendees = (school, jwt, adEid) => {
  return httpPut(jwt, `${ENDPOINTS[school.region].activity}/activity-date/${adEid}/delete-attendees`, {
  });
};

